import classNames from 'classnames';
import { memo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { hasMobileUserAgent } from '@carvana/stc-utilities';

import type { MfeChildProps } from '../../mfeTypes';

import { isOfferFooterGated } from '../../utilities/transposedRoutes';
import ErrorBoundary from './ErrorBoundary';
import QualiSurvey from './QualiSurvey';
import { useContentSettled } from './contentSettled';

const DisplayController = styled.div`
  &.transposeContent {
    display: none;
  }

  @media print {
    display: none;
  }
`;

// Transparent wrapper (no box) giving the QualiSurvey mount a stable, digit-free id
// so layout-shift attribution names it instead of a positional selector.
const QualiSlot = styled.div`
  display: contents;
`;

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  margin: 0 auto;
  &.nativeMobile {
    height: max-content;
  }
`;

const Robe = styled.main`
  background: ${({ theme }) => theme.carvana.white.primary};
  justify-self: center;
  margin-left: auto;
  margin-right: auto;
  max-width: 1800px;
  width: 100%;

  @media print {
    background-color: white;
  }
`;

const chatContext = {
  contextType: 'carvana_trade'
};

const DummyHeader = styled.div`
  background-color: var(--canvas-default);
  height: 129px;
  max-width: 1800px;
  margin: 0 auto;

  @media (min-width: 768px) {
    height: 79px;
  }
`;

const Header = memo(() => (
  <MfeReactComponent component="default" module="HeaderModule" loading={<DummyHeader />}>
    {({ Component: HeaderComponent }: MfeChildProps) => (
      <HeaderComponent
        environment={process.env.CARVANA_APP_ENVIRONMENT}
        chatContext={chatContext}
        useProvider
      />
    )}
  </MfeReactComponent>
));

const Footer = memo(() => (
  <MfeReactComponent component="default" module="FooterModule">
    {({ Component: FooterComponent }: MfeChildProps) => (
      <FooterComponent environment={process.env.CARVANA_APP_ENVIRONMENT} withoutProvider />
    )}
  </MfeReactComponent>
));

type BasePageProps = {
  children: React.ReactNode;
  transpose?: boolean;
};

const BasePage = ({ children, transpose = false }: BasePageProps) => {
  // Derived from props every render so it's synchronously correct when the
  // transpose prop flips mid-mount (e.g. nav into a wizard route).
  // hasMobileUserAgent returns true inside the Carvana native iOS/Android
  // webview, where the app provides its own header/back chrome around us.
  const transposeContent = hasMobileUserAgent() || transpose;

  // On offer-flow routes, defer the Footer mount until the hosted MFE
  // dispatches `shell:content-settled` (or the timeout fires). Footer takes
  // its position from where the content ends, so mounting it while content
  // is still arriving would push it down with each chunk. Waiting until
  // content has settled means Footer appears once below the final content
  // and never moves. No space reservation while gated — an empty slot has
  // zero height, so nothing is pushed when Footer eventually mounts.
  const { pathname } = useLocation();
  const gateFooter = isOfferFooterGated(pathname);
  const footerReady = useContentSettled({ enabled: gateFooter, timeoutMs: 8000 });

  return (
    <WizardContainer
      id="wizard-container"
      className={classNames({ nativeMobile: hasMobileUserAgent(window.navigator) })}
    >
      <QualiSlot id="shell-quali-slot">
        <QualiSurvey />
      </QualiSlot>
      <DisplayController id="shell-header-slot" className={classNames({ transposeContent })}>
        <Header />
      </DisplayController>
      <ErrorBoundary>
        <Robe id="sell-to-carvana">{children}</Robe>
      </ErrorBoundary>
      <DisplayController id="shell-footer-slot" className={classNames({ transposeContent })}>
        {footerReady ? <Footer /> : null}
      </DisplayController>
    </WizardContainer>
  );
};

export default BasePage;
